import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useAuthStore } from '../stores/auth-store';
import { getAuthUser } from '@/shared/api/client/authentication';

const isValidAuthUser = (value: unknown): value is { userId: string } => {
    return typeof value === 'object' &&
        value !== null &&
        'userId' in value &&
        typeof (value as { userId?: unknown }).userId === 'string' &&
        (value as { userId: string }).userId.trim().length > 0;
};

/**
 * Custom hook for authentication operations
 * Provides login, logout, and authentication checking functionality
 * Uses session-based authentication with cookies
 */
export const useAuth = () => {
    const navigate = useNavigate();
    const { user, isLoading, error, setUser, setLoading, setError, clearAuth } = useAuthStore();

    /**
     * Check if user is authenticated by fetching user data from backend
     * Uses session cookies for authentication
     */
    const checkAuth = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const userData = await getAuthUser();

            if (!isValidAuthUser(userData)) {
                clearAuth();
                localStorage.removeItem('auth-storage');
                return false;
            }

            setUser(userData);
            
            return true;
        } catch (err: any) {
            // If we get 401/403/302, user is not authenticated
            if (err?.response?.status === 401 || 
                err?.response?.status === 403 || 
                err?.response?.status === 302) {
                clearAuth();
                localStorage.removeItem('auth-storage');
                return false;
            }
            
            // For network errors or other issues, also clear auth to be safe
            console.error('Error checking authentication:', err);
            clearAuth();
            localStorage.removeItem('auth-storage');
            setError(err);
            return false;
        } finally {
            setLoading(false);
        }
    }, [setUser, setLoading, setError, clearAuth]);

    /**
     * Logout user and redirect to login page
     * Calls backend logout endpoint to invalidate session
     */
    const logout = useCallback(async () => {
        try {
            // Call backend logout endpoint
            await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include',
            });
            
            // Clear local auth state
            clearAuth();
            
            // Clear persisted storage
            localStorage.removeItem('auth-storage');
            
            // Show success message
            message.success('Logged out successfully');
            
            // Redirect to login
            navigate('/login', { replace: true });
        } catch (err) {
            console.error('Logout error:', err);
            // Clear auth state even if backend call fails
            clearAuth();
            localStorage.removeItem('auth-storage');
            navigate('/login', { replace: true });
        }
    }, [clearAuth, navigate]);

    /**
     * Check if user is authenticated (synchronous check based on store state)
     */
    const isAuthenticated = useCallback(() => {
        return !!user;
    }, [user]);

    return {
        user,
        isLoading,
        error,
        isError: !!error,
        checkAuth,
        logout,
        isAuthenticated,
    };
};
